import i18next, { type i18n as I18nInstance } from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { initReactI18next } from 'react-i18next';

import {
  DEFAULT_LOCALE,
  DEFAULT_NAMESPACE,
  LOCALE_STORAGE_KEY,
  NAMESPACES,
  SUPPORTED_LOCALES,
} from './config.js';
import { resources } from './resources.js';

/**
 * A dedicated instance rather than the i18next singleton, so a second app in this monorepo can
 * hold its own without the two fighting over global state. Pair it with `<I18nextProvider>`.
 *
 * The explicit annotation is required: this package is consumed as source with `declaration: true`,
 * and an inferred type here would reference i18next's internal paths.
 */
export const i18n: I18nInstance = i18next.createInstance();

/** Call once, await it, then render. Idempotent — safe under React StrictMode double-invoke. */
export function initI18n(): Promise<I18nInstance> {
  if (i18n.isInitialized) return Promise.resolve(i18n);

  return i18n
    .use(LanguageDetector)
    .use(initReactI18next)
    .init({
      resources,
      fallbackLng: DEFAULT_LOCALE,
      supportedLngs: [...SUPPORTED_LOCALES],
      ns: [...NAMESPACES],
      defaultNS: DEFAULT_NAMESPACE,
      // Collapse regional tags: a browser reporting `zh-CN` or `en-GB` resolves to `zh` / `en`
      // rather than missing the catalog entirely.
      load: 'languageOnly',
      // React already escapes everything it renders.
      interpolation: { escapeValue: false },
      detection: {
        // An explicit choice wins over the browser's preference, and persists across visits.
        order: ['localStorage', 'navigator', 'htmlTag'],
        lookupLocalStorage: LOCALE_STORAGE_KEY,
        caches: ['localStorage'],
      },
      // Resources are bundled, so there is nothing to suspend on.
      react: { useSuspense: false },
    })
    .then(() => i18n);
}
